import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Info, Calendar, FileText, BarChart3, Gift } from 'lucide-react';
import { getUser, isAdmin, canApproveLeave } from '@/lib/auth';
import api from '@/lib/api';

import { LeaveEntitlement, LeaveRegister, AllBalancesTable } from './leave/LeaveInfoTab';
import LeaveRules from './leave/LeaveRules';
import { ApplyLeaveDialog, MyRequests, TeamRequests } from './leave/ApplyApprovalTab';
import LeavePlannerTab from './leave/LeavePlannerTab';
import LeaveReportsTab from './leave/LeaveReportsTab';
import { MLPApplyCard, MLPReportTable } from './leave/MLPLeaveTab';
import CompOffTab from './leave/CompOffTab';

export default function LeavePage() {
  const [balances, setBalances] = useState(null);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('info');
  const [infoTab, setInfoTab] = useState('entitlement');
  const [applyTab, setApplyTab] = useState('my-requests');
  const admin = isAdmin();
  const approver = canApproveLeave();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        api.get('/api/leave/balances'),
        api.get('/api/leave/requests'),
      ]);
      setBalances(Array.isArray(b.data) ? null : b.data);
      setAllRequests(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingCount = allRequests.filter(r => r.status === 'pending' || r.status === 'manager_approved').length;

  if (loading) return <div className="space-y-4"><Skeleton className="h-12" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-5" data-testid="leave-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// LEAVE MANAGEMENT</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Leave Management</h1>
          <p className="text-sm text-slate-500">Manage leaves, view entitlements, and track requests</p>
        </div>
        <ApplyLeaveDialog onSuccess={load} />
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-white border border-slate-200 h-10">
          <TabsTrigger value="info" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-info">
            <Info className="w-3.5 h-3.5" />Leave Info
          </TabsTrigger>
          <TabsTrigger value="planner" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-planner">
            <Calendar className="w-3.5 h-3.5" />Planner
          </TabsTrigger>
          <TabsTrigger value="apply" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-apply">
            <FileText className="w-3.5 h-3.5" />Apply & Approval
            {approver && pendingCount > 0 && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] hover:bg-amber-100">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="comp-off" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-comp-off">
            <Gift className="w-3.5 h-3.5" />Comp-Off
          </TabsTrigger>
          {admin && (
            <TabsTrigger value="reports" className="gap-1.5 data-[state=active]:bg-[#0D2850] data-[state=active]:text-white" data-testid="tab-reports">
              <BarChart3 className="w-3.5 h-3.5" />Reports
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <Tabs value={infoTab} onValueChange={setInfoTab}>
            <TabsList className="bg-slate-50 border border-slate-200 h-9">
              <TabsTrigger value="entitlement" className="text-xs" data-testid="subtab-entitlement">Entitlement</TabsTrigger>
              <TabsTrigger value="register" className="text-xs" data-testid="subtab-register">Leave Register</TabsTrigger>
              <TabsTrigger value="rules" className="text-xs" data-testid="subtab-rules">Leave Rules</TabsTrigger>
              {admin && <TabsTrigger value="all-balances" className="text-xs" data-testid="subtab-all-balances">All Employees</TabsTrigger>}
            </TabsList>
            <TabsContent value="entitlement" className="mt-3">
              <LeaveEntitlement balances={balances} />
              <div className="mt-4"><MLPApplyCard /></div>
            </TabsContent>
            <TabsContent value="register" className="mt-3">
              <Card className="border-slate-200"><CardContent className="p-0"><LeaveRegister requests={allRequests} /></CardContent></Card>
            </TabsContent>
            <TabsContent value="rules" className="mt-3"><LeaveRules /></TabsContent>
            {admin && (
              <TabsContent value="all-balances" className="mt-3">
                <Card className="border-slate-200"><CardContent className="p-0"><AllBalancesTable /></CardContent></Card>
              </TabsContent>
            )}
          </Tabs>
        </TabsContent>

        <TabsContent value="planner" className="mt-4"><LeavePlannerTab /></TabsContent>

        <TabsContent value="comp-off" className="mt-4"><CompOffTab /></TabsContent>

        <TabsContent value="apply" className="mt-4">
          <Tabs value={applyTab} onValueChange={setApplyTab}>
            <TabsList className="bg-slate-50 border border-slate-200 h-9">
              <TabsTrigger value="my-requests" className="text-xs" data-testid="subtab-my-requests">My Requests</TabsTrigger>
              {approver && (
                <TabsTrigger value="team-requests" className="text-xs" data-testid="subtab-team-requests">
                  Team Requests
                  {pendingCount > 0 && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] hover:bg-amber-100">{pendingCount}</Badge>}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="my-requests" className="mt-3">
              <Card className="border-slate-200"><CardContent className="p-0"><MyRequests requests={allRequests} onRefresh={load} /></CardContent></Card>
            </TabsContent>
            {approver && (
              <TabsContent value="team-requests" className="mt-3">
                <Card className="border-slate-200"><CardContent className="p-0"><TeamRequests requests={allRequests} onRefresh={load} /></CardContent></Card>
              </TabsContent>
            )}
          </Tabs>
        </TabsContent>

        {admin && (
          <TabsContent value="reports" className="mt-4">
            <LeaveReportsTab />
            <MLPReportTable />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
